import { Switch, makeStyles } from '@material-ui/core'
import { useField, useFormikContext } from 'formik'
import pickBy from 'lodash/pickBy'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'

import { useActionRunner } from 'src/modules/notification'
import {
  Form,
  FormikDialog,
  FormikSelect,
  ProductList,
  Typography,
  FIELD_REQUIRED,
} from 'src/modules/ui'

import { usePurchasing } from '../../purchasing/hooks'
import { useSuppliers } from '../hooks'

const useStyles = makeStyles(theme => ({
  numberSelected: {
    marginLeft: theme.spacing(1),
  },
  productList: {
    height: 'calc(100vh - 375px)',
  },
  productListTable: {
    height: '100%',
  },
  stickHeader: {
    backgroundColor: theme.palette.fadedPurple.main,
  },
  selectAll: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}))

const mapToOption = ({ id, name }) => ({ value: id, label: name })

const AddProductsModal = ({ agreementId, products, trigger }) => {
  const { purchasingEnvironments } = usePurchasing()
  const { updateEnvironmentProducts } = useSuppliers()
  const [doUpdate] = useActionRunner(updateEnvironmentProducts, {
    successNotification: 'Products added to environment',
  })

  const handleSubmit = values => {
    const { environmentId, ...products } = values
    const productsForEnv = pickBy(products)
    const productIds = Object.keys(productsForEnv).map(id => Number(id))
    const params = {
      agreementId,
      environmentId,
      productIds,
    }
    return doUpdate(params)
  }

  const initialValues = products.reduce(
    (initial, product) => {
      initial[String(product.id)] = ''
      return initial
    },
    { environmentId: '' }
  )

  const validationSchema = Yup.object().shape({
    environmentId: Yup.string().required(FIELD_REQUIRED),
  })

  return (
    <FormikDialog
      initialValues={initialValues}
      fullWidth
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText="Add To Environment"
      title="Add Products To Environment"
      trigger={trigger}
      validationSchema={validationSchema}
    >
      <ProductForm
        products={products}
        purchasingEnvironments={purchasingEnvironments}
      />
    </FormikDialog>
  )
}

const FormikSwitch = props => {
  const [field] = useField(props)
  const { value } = field
  return <Switch {...field} {...props} checked={Boolean(value)} />
}

const ProductForm = ({ products, purchasingEnvironments }) => {
  const [lastEnvValue, setLastEnvValue] = useState('')
  const { setValues, values } = useFormikContext()
  const classes = useStyles()
  const { environmentId } = values

  useEffect(() => {
    if (environmentId !== lastEnvValue) {
      setLastEnvValue(environmentId)
      const currentValues = products.reduce((current, product) => {
        current[String(product.id)] = product.environments.includes(
          environmentId
        )
        return current
      }, {})
      // Set a product to true to illustrate
      setValues({
        ...values,
        ...currentValues,
      })
    }
  }, [environmentId])

  return (
    <Form>
      <FormikSelect
        fullWidth
        label="Purchasing Environment"
        margin="normal"
        name="environmentId"
        options={[...purchasingEnvironments.map(mapToOption)]}
      />
      {environmentId !== '' && (
        <>
          <div className={classes.selectAll}>
            <SelectAllProducts />
            <Typography>Select all</Typography>
            <NumberSelected className={classes.numberSelected} />
          </div>
          <ProductList
            className={classes.productList}
            tableClassName={classes.productListTable}
            navigateOnClick={false}
            productPreCell={({ product }) => (
              <FormikSwitch name={String(product.id)} />
            )}
            products={products}
            showSearch
            stickHeaderClassName={classes.stickHeader}
            useWindowScroll={false}
          />
        </>
      )}
    </Form>
  )
}

const NumberSelected = props => {
  const { values } = useFormikContext()
  const { environmentId, ...items } = values
  const selected = Object.values(items).reduce(
    (total, checked) => total + (checked ? 1 : 0),
    0
  )
  return (
    <Typography variant="body2" color="textSecondary" {...props}>
      ({selected} selected)
    </Typography>
  )
}

const SelectAllProducts = () => {
  const { setValues, values } = useFormikContext()
  const { environmentId, ...checkedStates } = values
  const allChecked = Object.values(checkedStates).every(val => !!val)

  const handleChange = event => {
    const { checked } = event.target
    const newValues = Object.keys(checkedStates).reduce(
      (values, id) => ({
        ...values,
        [id]: checked,
      }),
      {}
    )
    setValues({
      ...values,
      ...newValues,
    })
  }

  return <Switch checked={allChecked} onChange={handleChange} />
}

AddProductsModal.propTypes = {
  agreementId: PropTypes.number,
  trigger: PropTypes.func,
  products: PropTypes.array,
}

export default AddProductsModal
